import {Avatar, Space, Tag, Tooltip} from 'antd';
import React from 'react';
import NameWithLink from '../../components/NameWithLink';
import StatusMark from '../../components/StatusMark';
import StatusLine from '../../components/pages/SubscriptionsPage/StatusLine';
import {budgetProcurementTypeConstants, budgetStatusConstants} from '../../constants';
import {textHelpers} from './text.helpers';
import {OptionsList} from './form.helpers';
import {colorsStyles} from '../styles';
import routes from '../../routes/routes.json';

const budgetStatuses = {
  [budgetStatusConstants.CLOSED]: {
    key: 'budgetStatuses.closed',
    color: '#000'
  },
  [budgetStatusConstants.DRAFT]: {
    key: 'budgetStatuses.draft',
    color: '#722ED1'
  },
  [budgetStatusConstants.IN_REVIEW]: {
    key: 'budgetStatuses.inReview',
    color: '#FA8C16'
  },
  [budgetStatusConstants.REJECTED]: {
    key: 'budgetStatuses.rejected',
    color: '#FF4D4F'
  },
  [budgetStatusConstants.ACTIVE]: {
    key: 'budgetStatuses.inBudget',
    color: '#52C41A'
  },
  [budgetStatusConstants.APPROVED]: {
    key: 'budgetStatuses.approved',
    color: '#52C41A'
  },
  [budgetStatusConstants.OVER_BUDGET]: {
    key: 'budgetStatuses.overBudget',
    color: '#FA8C16'
  },
};

export const budgetSpendingKeys = {
  INVOICED: 'invoiced',
  SPENT: 'spend',
}

export const budgetSpendingData = {
  [budgetSpendingKeys.INVOICED]: {
    amountKey: 'invoiced_amount',
    color: '#B37FEB',
    labelKey: 'invoiced',
  },
  [budgetSpendingKeys.SPENT]: {
    amountKey: 'spent_amount',
    color: '#515A6B',
    labelKey: 'spent',
  },
}

export const BudgetStatusesOptions = (type) => {
  const allowedStatuses = {
    'active': budgetHelpers.getActiveBudgetStatuses(),
    'requests': budgetHelpers.getRequestedBudgetStatuses({excludeApproved: true})
  }[type] || [];

  const statuses = Object.keys(budgetStatuses).reduce((acc, value) => {
    acc[value] = budgetStatuses[value]?.key;
    return acc;
  }, {});

  return OptionsList(statuses).map(option => {
    let color = budgetStatuses[option.value]?.color;
    return {
      ...option,
      image: <StatusLine color={color} />
    }
  }).filter(status => allowedStatuses.includes(status.value));
}

export const budgetHelpers = {
  getActiveBudgetStatuses: () => [budgetStatusConstants.ACTIVE, budgetStatusConstants.CLOSED, budgetStatusConstants.OVER_BUDGET],
  getRequestedBudgetStatuses: ({excludeApproved = false} = {}) => {
    const statuses = [
      budgetStatusConstants.DRAFT,
      budgetStatusConstants.IN_REVIEW,
      budgetStatusConstants.REJECTED,
      budgetStatusConstants.APPROVED
    ];
    return excludeApproved ? statuses.filter(s => s !== budgetStatusConstants.APPROVED) : statuses;
  },
  getGroupedBudgetsByBudget: (budgets = []) => {
    const groupedBudgets = [];
    const budgetKeyName = 'budget_id';
    budgets.forEach(budget => {
      const budgetId = budget[budgetKeyName];
      if (budgetId) {
        if (!groupedBudgets.find(b => b.budget_id === budgetId)) {
          groupedBudgets.push({
            id: `${budgetId}-grouped`,
            isGrouped: true,
            budget_id: budgetId,
            name: budget?.budget_name || 'Unnamed Budget',
            expanded_ids: budgets.filter(b => b[budgetKeyName] === budgetId).map(({id}) => id) || [],
          });
        }
      }
      groupedBudgets.push(budget);
    });

    return groupedBudgets;
  },
  getGroupedActiveBudgets: ({rows = [], budgets = []} = {}) => {
    return (rows || []).map(row => {
      if (!row.isGrouped) return row;
      const budget = budgets.find(b => b.id === row.budget_id);
      const limitAmount = budget?.limit_amount || 0;
      const utilizedAmount = budget?.utilized_amount || 0;
      const availableAmount = limitAmount - utilizedAmount;
      const utilizationPercentage = limitAmount > 0 ? Math.round((utilizedAmount / limitAmount) * 100) : 0;
      return {
        ...row,
        available_amount: availableAmount,
        name: budget?.name || row?.name,
        limit_amount: limitAmount,
        reported_by: budget?.reported_by,
        utilization_percentage: utilizationPercentage
      };
    });
  },
  getOwner: (user) => {
    if (!user) return null;
    const name = user?.full_name || user?.email;
    return (
      <Tooltip title={name}>
        <Avatar src={user?.logo || undefined} size={24} style={{fontSize: 12}}>
          {textHelpers.getInitials(name)}
        </Avatar>
      </Tooltip>
    )
  },
  getOwnerWithName: (user) => {
    if (!user) return null;
    const name = user?.full_name || user?.email;
    return (
      <Space size='small'>
        <Avatar src={user?.logo} size={24} style={{fontSize: 12}}>
          {textHelpers.getInitials(name)}
        </Avatar>
        <span>{name}</span>
      </Space>
    )
  },
  getBudgetName: (budget) => {
    const name = budget?.name;
    const number = budgetHelpers.getBudgetNumber(budget);
    return number ? `${number} / ${name}` : name;
  },
  getBudgetNumber: (budget) => ({
    [budgetProcurementTypeConstants.BUDGET_ITEM]: budget?.bi_number,
    [budgetProcurementTypeConstants.PURCHASE_ORDER]: budget?.po_number,
  })[budget?.procurement_type] || null,
  getBudgetStatusMark: (budget) => {
    if (!budget) return null;
    return (
      <StatusMark
        statusCode={budget.status}
        statuses={budgetStatuses}
      />
    );
  },
  getUsersList: (users = [], groupProps = {}) => {
    if (!users.length) return null;
    return <Avatar.Group className='d-flex' max={{ count: 6 }} size={22} {...groupProps}>
      {users.map((u, key) => {
        const logo = u.logo;
        const name = u.full_name || u.email;
        return (
          <Tooltip title={name} key={key}>
            <Avatar src={logo || undefined} style={{backgroundColor: logo ? 'unset' : '#BFBFBF'}}>
              {textHelpers.getInitials(name)}
            </Avatar>
          </Tooltip>
        )
      })}
    </Avatar.Group>
  },
  isEditableBudget: (budget) => {
    if (!budget) return false;
    return budgetStatusConstants.DRAFT === budget.status;
  },

  getProcurementTypeOptions: (t) => [
    {value: budgetProcurementTypeConstants.BUDGET_ITEM, label: t('budgetProcurementTypes.budget_item')},
    {value: budgetProcurementTypeConstants.PURCHASE_ORDER, label: t('budgetProcurementTypes.purchase_order')},
  ],
  getBudgetOptions: (budgets = []) => {
    if (!budgets.length) return [];
    const sorted = [...budgets].sort((a, b) => a.name.localeCompare(b.name));
    return sorted.map(budget => ({
      value: budget.id,
      label: budgetHelpers.getBudgetWithUtilizationTag(budget)
    }));
  },
  getBudgetWithUtilizationTag: (budget) => {
    if (!budget) return null;
    const utilization = ((budget?.utilized_amount || 0) / (budget?.limit_amount || 0) * 100).toFixed(0);
    const isOverUtilized = utilization > 100;
    const style = {
      color: isOverUtilized ? colorsStyles.functional_danger : colorsStyles.neutral_7,
      backgroundColor: isOverUtilized ? 'rgba(255,120,117,0.6)' : colorsStyles.neutral_2,
      borderRadius: 16,
      lineHeight: '20px',
      margin: 0,
      padding: '2px 8px'
    }
    return (
      <Space size='small' align='center' className='w-100 justify-space-between'>
        <span>{budget.name}</span>
        <Tag bordered={false} style={style}>{utilization}%</Tag>
      </Space>
    )
  },
  getBudgetLabel: (budgets = [], budgetId) => {
    const budget = budgets.find(budget => budget.id === budgetId);
    return budget ? budgetHelpers.getBudgetWithUtilizationTag(budget) : budgetId;
  },
  getBudgetNameWithLink: (budget) => {
    if (!budget) return null;
    const name = budgetHelpers.getBudgetName(budget);
    return <NameWithLink name={name} url={`${routes.budgetsActive}/${budget.id}`} />;
  },
  calculateBudgetProgress: ({totalSpent = 0, budgetLimit = 0, spent = 0, clearUtilization = false}) => {
    const percentage = budgetLimit ? (totalSpent / budgetLimit) * 100 : 0;
    const percentageSpent = budgetLimit ? (spent / budgetLimit) * 100 : 0;

    const progressProps = {
      percent: percentage > 100 ? 100 : percentage,
      success: {percent: percentageSpent, strokeColor: budgetSpendingData[budgetSpendingKeys.SPENT].color}
    }
    const remainingBudget = {
      amount: budgetLimit - totalSpent,
      percentage: clearUtilization ? percentage > 100 ? percentage - 100 : percentage : percentage,
      type: (budgetLimit - totalSpent) >= 0 ? 'positive' : 'negative'
    }
    return { remainingBudget, progressProps };
  },
  isPurchaseOrderBudget: (budget) => {
    return budget?.procurement_type === budgetProcurementTypeConstants.PURCHASE_ORDER;
  },
  isDraftBudget: (budget) => {
    return budget?.status === budgetStatusConstants.DRAFT;
  }
}
